import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axiosInstance from '../config/axios';
import { useAuth } from './AuthContext';

/**
 * SettingsContext
 * مركز إدارة جميع إعدادات النظام من Backend
 * جميع الإعدادات تجلب من قاعدة البيانات عبر API
 * 
 * ✅ يتجاهل محاولات الجلب إذا لم يكن المستخدم مسجل دخول
 */

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 دقائق

  // جلب الإعدادات من Backend (العامة متاحة للجميع، الكاملة للمشرفين فقط)
  const fetchSettings = useCallback(async () => {
    // فقط المشرفون يملكون صلاحية جلب admin settings
    let endpoint = '/public/settings'; // الإعدادات العامة (متاحة للجميع)
    
    // ✅ تحسين: تخطي الطلب إذا كانت البيانات في الـ cache
    const now = Date.now();
    if (lastFetched && (now - lastFetched) < CACHE_DURATION && Object.keys(settings).length > 0) {
      return; // البيانات طازة، لا حاجة للطلب
    }
    
    try {
      setLoading(true);
      setError(null);
      
      if (isAuthenticated && user?.role === 'admin') {
        endpoint = '/admin/settings'; // الحصول على الإعدادات الكاملة للمشرفين فقط
      }
      
      const response = await axiosInstance.get(endpoint);
      
      if (response?.data?.data) {
        setSettings(response.data.data);
        setLastFetched(now); // ✅ تسجيل وقت آخر جلب
      }
    } catch (err) {
      // محاولة جلب الإعدادات العامة على الأقل إذا فشل جلب الإعدادات الكاملة
      if (endpoint !== '/public/settings') {
        try {
          const fallbackResponse = await axiosInstance.get('/public/settings');
          if (fallbackResponse?.data?.data) {
            setSettings(fallbackResponse.data.data);
            setLastFetched(now);
          }
        } catch (fallbackErr) {
          console.error('Failed to fetch public settings:', fallbackErr.message);
          setSettings({});
          setError(fallbackErr.message);
        }
      } else {
        setSettings({});
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.role, lastFetched, settings, CACHE_DURATION]);

  // جلب الإعدادات عند تحميل المكون أو عند تغير حالة المصادقة
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings, isAuthenticated]);

  // إعادة تحميل الإعدادات (تُستخدم بعد تحديث الإعدادات من SystemSettings)
  const refreshSettings = useCallback(async () => {
    await fetchSettings();
  }, [fetchSettings]);

  // تحديث الإعدادات
  const updateSettings = useCallback(async (updatedSettings) => {
    // ✅ حماية: التحقق من أن المستخدم admin
    if (user?.role !== 'admin') {
      throw new Error('Unauthorized: Only admins can update settings');
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.put('/admin/settings', updatedSettings);
      
      if (response?.data?.data) {
        setSettings(response.data.data);
        setLastFetched(Date.now()); // ✅ تحديث وقت الـ cache
      }
      
      return response.data;
    } catch (err) {
      console.error('❌ Update failed:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  // دالة للحصول على قيمة محددة
  const getSetting = useCallback((key, defaultValue = null) => {
    return settings[key] ?? defaultValue;
  }, [settings]);

  // دالة للحصول على رقم الواتساب & بناء الرابط
  const getWhatsAppNumber = useCallback(() => {
    return getSetting('platform_whatsapp');
  }, [getSetting]);

  const getWhatsAppLink = useCallback((message) => {
    const number = getWhatsAppNumber();
    // ✅ حماية: التحقق من وجود الرقم قبل بناء الرابط
    if (!number) {
      console.warn('WhatsApp number not configured');
      return null;
    }
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${number}?text=${encodedMessage}`;
  }, [getWhatsAppNumber]);

  const openWhatsApp = useCallback((message) => {
    const link = getWhatsAppLink(message);
    // ✅ حماية: التحقق من وجود الرابط قبل الفتح
    if (link) {
      window.open(link, '_blank');
    }
  }, [getWhatsAppLink]);

  // ⭐ دوال المعلومات الأساسية للمنصة
  const getPlatformName = useCallback(() => {
    return getSetting('platform_name', 'منصتي');
  }, [getSetting]);

  // دالة للحصول على البريد الإلكتروني
  const getSupportEmail = useCallback(() => {
    return getSetting('support_email', 'support@munasati.com');
  }, [getSetting]);

  // دالة للحصول على الهاتف العام
  const getContactPhone = useCallback(() => {
    return getSetting('general_contact_phone', '01012345678');
  }, [getSetting]);

  // ⭐ دوال الهوية البصرية
  const getSiteLogo = useCallback(() => {
    return getSetting('site_logo_url', '/logo.png');
  }, [getSetting]);

  const getSiteFavicon = useCallback(() => {
    return getSetting('site_favicon_url', '/favicon.ico');
  }, [getSetting]);

  const getPrimaryColor = useCallback(() => {
    return getSetting('brand_primary_color', '#0066cc');
  }, [getSetting]);

  const getSecondaryColor = useCallback(() => {
    return getSetting('brand_secondary_color', '#f3f4f6');
  }, [getSetting]);

  // ⭐ دوال التواصل الاجتماعي
  const getSocialLinks = useCallback(() => {
    return {
      facebook: getSetting('social_facebook_url', 'https://facebook.com'),
      twitter: getSetting('social_twitter_url', 'https://twitter.com'),
      youtube: getSetting('social_youtube_url', 'https://youtube.com'),
      instagram: getSetting('social_instagram_url', 'https://instagram.com'),
      linkedin: getSetting('social_linkedin_url', 'https://linkedin.com')
    };
  }, [getSetting]);

  // ⭐ دوال الروابط العامة
  const getSiteBaseUrl = useCallback(() => {
    return getSetting('site_base_url', 'https://munasati.online');
  }, [getSetting]);

  const getContactPageUrl = useCallback(() => {
    return getSetting('site_contact_page_url', 'https://munasati.online/contact');
  }, [getSetting]);

  const getTermsUrl = useCallback(() => {
    return getSetting('site_terms_url', 'https://munasati.online/terms');
  }, [getSetting]);

  const getPrivacyUrl = useCallback(() => {
    return getSetting('site_privacy_url', 'https://munasati.online/privacy');
  }, [getSetting]);

  // ✅ تحسين الأداء: استخدام useMemo لتجنب إعادة تكوين الكائن في كل render
  const value = useMemo(() => ({
    settings,
    loading,
    error,
    fetchSettings,
    refreshSettings,
    updateSettings,
    getSetting,
    // Platform Info
    getPlatformName,
    // Contact & WhatsApp
    getWhatsAppNumber,
    getWhatsAppLink,
    openWhatsApp,
    getSupportEmail,
    getContactPhone,
    // Branding
    getSiteLogo,
    getSiteFavicon,
    getPrimaryColor,
    getSecondaryColor,
    // Social Media
    getSocialLinks,
    // General URLs
    getSiteBaseUrl,
    getContactPageUrl,
    getTermsUrl,
    getPrivacyUrl
  }), [settings, loading, error, fetchSettings, refreshSettings, updateSettings, getSetting, getPlatformName, getWhatsAppNumber, getWhatsAppLink, openWhatsApp, getSupportEmail, getContactPhone, getSiteLogo, getSiteFavicon, getPrimaryColor, getSecondaryColor, getSocialLinks, getSiteBaseUrl, getContactPageUrl, getTermsUrl, getPrivacyUrl]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

/**
 * Custom Hook: استخدام إعدادات النظام
 * @returns {Object} settings context
 */
export const useSettings = () => {
  const context = useContext(SettingsContext);
  
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  
  return context;
};

export default SettingsContext;
